import { makeAutoObservable, runInAction } from "mobx";
import eventsAPIHandler from "../handlers/EventsAPIHandler";

class DeviceEventStore {
  connectionEventsMap = new Map(); // month -> deviceId -> connection events array
  valveEventsMap = new Map(); // month -> deviceId -> { v1: [], v2: [] }

  lastEventIdMap = new Map(); // deviceId -> last event id

  loading = false;
  error = null;

  constructor() {
    makeAutoObservable(this);
  }

  getMonthKey(timestamp) {
    if (!timestamp) return "unknown";

    let iso;
    if (typeof timestamp === "string") {
      iso = timestamp;
    } else if (timestamp instanceof Date) {
      iso = timestamp.toISOString();
    } else if (timestamp?.$date) {
      iso = timestamp.$date;
    } else {
      console.warn("Unrecognized timestamp format:", timestamp);
      return "unknown";
    }

    return iso.slice(0, 7); // "YYYY-MM"
  }

  async fetchDeviceEvents(deviceId) {
    if (!deviceId) return;

    this.loading = true;
    this.error = null;

    try {
      // const lastId = this.lastEventIdMap.get(deviceId) || null;

      let lastId = this.lastEventIdMap.get(deviceId) || null;
      if (lastId && !/^[a-fA-F0-9]{24}$/.test(lastId)) {
        lastId = null;
      }
      const newEvents = await eventsAPIHandler.fetchEventsForDevice(
        deviceId,
        lastId
      );

      console.log(`Here are the events for ${deviceId}: `, newEvents);

      runInAction(() => {
        const seenDevices = new Set();

        for (const event of newEvents) {
          const month = this.getMonthKey(event.timestamp);
          const id = event._id?.$oid;
          const eventType = event?.event_type;

          if (eventType === "connection") {
            if (!this.connectionEventsMap.has(month)) {
              this.connectionEventsMap.set(month, new Map());
            }
            const monthMap = this.connectionEventsMap.get(month);
            if (!monthMap.has(deviceId)) {
              monthMap.set(deviceId, []);
            }
            const connList = monthMap.get(deviceId);
            if (!connList.some((e) => e._id?.$oid === id)) {
              connList.unshift(event);
            }
          } else if (eventType === "valve_state") {
            const valve =
              event.payload?.valve || event.metadata?.valve || "unknown";
            if (!["v1", "v2"].includes(valve)) continue;
            if (!this.valveEventsMap.has(month)) {
              this.valveEventsMap.set(month, new Map());
            }
            const monthMap = this.valveEventsMap.get(month);
            if (!monthMap.has(deviceId)) {
              monthMap.set(deviceId, { v1: [], v2: [] });
            }
            const deviceValveMap = monthMap.get(deviceId);
            if (!deviceValveMap[valve].some((e) => e._id?.$oid === id)) {
              deviceValveMap[valve].unshift(event);
            }
          }

          if (!seenDevices.has(deviceId)) {
            this.lastEventIdMap.set(deviceId, event?._id?.$oid);
            seenDevices.add(deviceId);
          }
        }

        if (newEvents.length > 0) {
          const lastEvent = newEvents[0];
          this.lastEventIdMap.set(deviceId, lastEvent?._id?.$oid);
        }
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  getConnectionEvents(month, deviceId) {
    return this.connectionEventsMap.get(month)?.get(deviceId) || [];
  }

  getValveEvents(deviceId, month) {
    return this.valveEventsMap.get(month)?.get(deviceId) || { v1: [], v2: [] };
  }

  getMonthlyDisconnectCount(month, deviceId) {
    const events = this.getConnectionEvents(month, deviceId);
    const len = events.length;
    if (len === 0) return 0;

    const firstStatus = events[0]?.payload?.status?.toLowerCase();
    const lastStatus = events[len - 1]?.payload?.status?.toLowerCase();

    const firstIndex = firstStatus === "disconnected" ? 0 : 1;
    const lastIndex = lastStatus === "disconnected" ? len - 1 : len - 2;

    if (firstIndex > lastIndex) return 0;
    return Math.ceil((lastIndex - firstIndex + 1) / 2);
  }

  getMonthlyValveCloseCount(month, deviceId, valve) {
    const events = this.getValveEvents(deviceId, month)[valve] || [];
    const len = events.length;
    if (len === 0) return 0;

    const firstStatus = events[0]?.payload?.state?.toLowerCase();
    const lastStatus = events[len - 1]?.payload?.state?.toLowerCase();

    const firstIndex = firstStatus === "closed" ? 0 : 1;
    const lastIndex = lastStatus === "closed" ? len - 1 : len - 2;

    if (firstIndex > lastIndex) return 0;
    return Math.ceil((lastIndex - firstIndex + 1) / 2);
  }

  injectMockData(mockEvents = []) {
    runInAction(() => {
      for (const event of mockEvents) {
        const deviceId = event.device_id;
        const month = this.getMonthKey(event.timestamp);
        const id = event._id?.$oid || `mock_${Date.now()}_${Math.random()}`;

        if (event.event_type === "connection") {
          if (!this.connectionEventsMap.has(month)) {
            this.connectionEventsMap.set(month, new Map());
          }
          const monthMap = this.connectionEventsMap.get(month);
          if (!monthMap.has(deviceId)) {
            monthMap.set(deviceId, []);
          }
          const connList = monthMap.get(deviceId);
          if (!connList.some((e) => (e._id?.$oid || e._id) === id)) {
            connList.unshift({ ...event, _id: { $oid: id } });
          }
          this.lastEventIdMap.set(deviceId, id);
        }

        if (event.event_type === "valve_state") {
          const valve = event.valve || event.metadata?.valve || "unknown";
          if (!["v1", "v2"].includes(valve)) continue;

          if (!this.valveEventsMap.has(month)) {
            this.valveEventsMap.set(month, new Map());
          }
          const monthMap = this.valveEventsMap.get(month);
          if (!monthMap.has(deviceId)) {
            monthMap.set(deviceId, { v1: [], v2: [] });
          }
          const vMap = monthMap.get(deviceId);
          if (!vMap[valve].some((e) => (e._id?.$oid || e._id) === id)) {
            vMap[valve].unshift({ ...event, _id: { $oid: id } });
          }
          this.lastEventIdMap.set(deviceId, id);
        }
      }
    });
  }
}

const deviceEventStore = new DeviceEventStore();
// deviceEventStore.injectMockData([
//   {
//     _id: { $oid: "mock_conn_001" },
//     device_id: "373137381932343431332F57",
//     event_type: "connection",
//     timestamp: new Date().toISOString(),
//     source_log_id: 7116457,
//     payload: {
//       status: "connected",
//       trigger: "first-seen",
//       metadata: {
//         disconnected_duration_sec: null,
//       },
//     },
//   },
//   {
//     _id: { $oid: "mock_valve_v1" },
//     device_id: "373137381932343431332F57",
//     event_type: "valve_state",
//     timestamp: new Date().toISOString(),
//     metadata: {
//       valve: "v1",
//       position: "open",
//     },
//   },
//   {
//     _id: { $oid: "mock_valve_v2" },
//     device_id: "dev123",
//     event_type: "valve_state",
//     timestamp: new Date().toISOString(),
//     metadata: {
//       valve: "v2",
//       position: "closed",
//     },
//   },
// ]);
export default deviceEventStore;
